<script setup lang="ts">
import { ref, shallowRef } from "vue";
import { useRouter } from "vue-router";
import { Eye, EyeOff, ShieldCheck } from "lucide-vue-next";
import AuthPageShell from "@/components/auth/AuthPageShell.vue";
import ExternalMfaChallengeForm from "@/components/auth/ExternalMfaChallenge.vue";
import ExternalTotpEnrollment from "@/components/auth/ExternalTotpEnrollment.vue";
import LogoMark from "@/components/LogoMark.vue";
import { api } from "@/services/api";
import {
  completeExternalMfaSignIn,
  finishExternalTotpEnrollment,
  refreshAuthToken,
  signInExternal,
  signOutUser,
  startExternalTotpEnrollment,
  type ExternalMfaChallenge,
  type ExternalTotpEnrollment as ExternalTotpEnrollmentState,
} from "@/services/firebase";
import { useWorkspaceStore } from "@/stores/workspace";
import { externalMfaErrorMessage } from "@/utils/auth-errors";
import { consumeInvitationSecret } from "@/utils/invitations";

const router = useRouter();
const workspace = useWorkspaceStore();
const invitation = consumeInvitationSecret();
const email = ref("");
const password = ref("");
const showPassword = ref(false);
const loading = ref(false);
const error = ref("");
const mfaChallenge = ref<ExternalMfaChallenge | null>(null);
const mfaEnrollment = shallowRef<ExternalTotpEnrollmentState | null>(null);

async function redeemInvitation() {
  if (!invitation) return;
  const result = (await api.redeemInvitation(invitation)) as {
    route: string;
  };
  await refreshAuthToken();
  await workspace.refreshFromApi();
  await router.replace(result.route);
}

async function acceptInvitation() {
  if (!invitation || !email.value.trim() || !password.value) return;
  loading.value = true;
  error.value = "";
  try {
    const signIn = await signInExternal(email.value, password.value);
    if (signIn.status === "mfa-required") {
      mfaChallenge.value = signIn.challenge;
      return;
    }
    if (signIn.status === "mfa-enrollment-required") {
      mfaEnrollment.value = await startExternalTotpEnrollment(password.value);
      return;
    }
    await redeemInvitation();
  } catch {
    await signOutUser().catch(() => undefined);
    error.value =
      "We could not accept this invitation. Check the invited email and password. If the invitation expired or was replaced, ask the project owner to resend it.";
  } finally {
    loading.value = false;
  }
}

async function completeEnrollment(code: string) {
  if (!mfaEnrollment.value) return;
  loading.value = true;
  error.value = "";
  try {
    await finishExternalTotpEnrollment(mfaEnrollment.value, code);
    mfaEnrollment.value = null;
    await signOutUser();
    const signIn = await signInExternal(email.value, password.value);
    if (signIn.status !== "mfa-required")
      throw new Error("planner/mfa-challenge-required");
    mfaChallenge.value = signIn.challenge;
  } catch (enrollmentError) {
    error.value = externalMfaErrorMessage(enrollmentError, "enroll");
  } finally {
    loading.value = false;
  }
}

async function verifyExternalMfa(value: { factorUid: string; code: string }) {
  if (!mfaChallenge.value) return;
  loading.value = true;
  error.value = "";
  try {
    await completeExternalMfaSignIn(
      mfaChallenge.value,
      value.factorUid,
      value.code,
    );
    await redeemInvitation();
  } catch (mfaError) {
    error.value = externalMfaErrorMessage(mfaError, "sign-in");
  } finally {
    loading.value = false;
  }
}

async function cancelExternalMfa() {
  mfaChallenge.value = null;
  mfaEnrollment.value = null;
  error.value = "";
  password.value = "";
  await signOutUser().catch(() => undefined);
}
</script>

<template>
  <AuthPageShell labelled-by="accept-title">
    <div class="accept-content">
      <div class="accept-heading">
        <LogoMark class="accept-mark" tone="dark" />
        <div class="accept-icon" aria-hidden="true">
          <ShieldCheck :size="22" />
        </div>
        <h2 id="accept-title">Accept your project invitation</h2>
        <p>Sign in with the email address that was invited.</p>
      </div>

      <div v-if="!invitation" class="message message--error" role="alert">
        <strong>This invitation link is no longer valid.</strong>
        <span>Ask the project owner to resend your invitation.</span>
      </div>

      <ExternalMfaChallengeForm
        v-else-if="mfaChallenge"
        :challenge="mfaChallenge"
        :busy="loading"
        :error="error"
        action-label="Verify and accept invitation"
        @verify="verifyExternalMfa"
        @cancel="cancelExternalMfa"
      />

      <ExternalTotpEnrollment
        v-else-if="mfaEnrollment"
        :enrollment="mfaEnrollment"
        :busy="loading"
        :error="error"
        action-label="Verify and accept invitation"
        @verify="completeEnrollment"
        @cancel="cancelExternalMfa"
      />

      <form v-else class="accept-form" @submit.prevent="acceptInvitation">
        <div class="field">
          <label for="accept-email">Email</label>
          <input
            id="accept-email"
            v-model="email"
            class="input"
            type="email"
            autocomplete="username"
            required
          />
        </div>
        <div class="field">
          <label for="accept-password">Password</label>
          <div class="password-field">
            <input
              id="accept-password"
              v-model="password"
              class="input"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="current-password"
              required
            />
            <button
              class="password-toggle"
              type="button"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
              :aria-pressed="showPassword"
              @click="showPassword = !showPassword"
            >
              <EyeOff v-if="showPassword" :size="17" />
              <Eye v-else :size="17" />
            </button>
          </div>
        </div>
        <div v-if="error" class="message message--error" role="alert">
          {{ error }}
        </div>
        <button
          class="button button--primary accept-submit"
          type="submit"
          :disabled="loading || !email.trim() || !password"
        >
          {{
            loading ? "Accepting invitation…" : "Sign in and accept invitation"
          }}
        </button>
      </form>

      <RouterLink class="back-link" to="/login">Back to sign in</RouterLink>
    </div>
  </AuthPageShell>
</template>

<style scoped>
.accept-content {
  width: min(430px, 100%);
  display: grid;
  gap: 24px;
}
.accept-heading {
  display: grid;
  justify-items: center;
  text-align: center;
}
.accept-mark {
  margin-bottom: 20px;
}
.accept-mark :deep(.logo-mark__wordmark) {
  width: 132px;
}
.accept-icon {
  width: 44px;
  height: 44px;
  margin-bottom: 14px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--brand-strong);
  background: var(--brand-soft);
}
.accept-heading h2 {
  font-size: 25px;
}
.accept-heading p {
  margin-top: 7px;
  color: var(--text-soft);
}
.accept-form {
  display: grid;
  gap: 16px;
}
.accept-form .input {
  min-height: 44px;
}
.password-field {
  position: relative;
}
.password-field .input {
  padding-right: 44px;
}
.password-toggle {
  position: absolute;
  top: 1px;
  right: 1px;
  width: 44px;
  height: calc(100% - 2px);
  display: grid;
  place-items: center;
  border-radius: 0 5px 5px 0;
  color: var(--text-soft);
  background: transparent;
  cursor: pointer;
}
.password-toggle:hover {
  color: var(--text);
  background: var(--sheet-strong);
}
.message {
  display: grid;
  gap: 3px;
  padding: 12px;
  border-radius: 7px;
}
.message--error {
  color: #7d2e2e;
  background: var(--danger-soft);
}
.accept-submit {
  min-height: 44px;
}
.back-link {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-self: center;
  color: var(--text-soft);
  font-size: 12px;
  text-decoration: underline;
  text-underline-offset: 3px;
}
</style>
